import { FilePurpose, languageDisplayCode, LanguagesEnum } from '@/api/enums';
import { equal } from '@/api/helpers';
import { Account } from '@/client/accounts';
import { CourseFormState, MaterialType } from '@/client/courses';
import { File } from '@/client/files';
import { Actions, Subjects } from '@/client/users';
import {
  LoadingStateType,
  RedirectPaths,
  RedirectPathsEnum,
} from '@/common/constants';
import { availableLanguages } from '@/common/constants/languages';
import { DialogContext } from '@/common/context';
import { createCourseSchema } from '@/components/courses/forms/validations';
import {
  FormikInput,
  FormikMultiSelect,
  FormikRichTextArea,
  FormikSwitch,
} from '@/components/form';
import { AccountSelectInput } from '@/components/form/selectors';
import { useAppSelector } from '@/hooks/store';
import { usePermission } from '@/hooks/usePermission';
import { selectCurrentAccount } from '@/store/features/account';
import { selectCurrentUser } from '@/store/features/users';
import { DialogType } from '@/system-settings/enums';
import { AppButton } from '@/ui/buttons';
import { AppCalendar } from '@/ui/calendar';
import { CourseFiles } from '@/ui/course-files';
import { AppFormAccordion } from '@/ui/form-accordion';
import { AppIconTooltip } from '@/ui/icon-tooltip';
import { ImageUpload } from '@/ui/image-upload';
import { FlexContainer, FormContainer } from '@/ui/styled-ui';
import { TabSwitchButtons } from '@/ui/tab-switch-buttons';
import {
  branchAdminCheck,
  courseLanguagesOptions,
  isDemoRestrictedMode,
  posterCategoriesOptions,
  sortAlphabetically,
} from '@/utils/helpers';
import { Field, Form, Formik, FormikValues } from 'formik';
import { MultiSelectChangeEvent } from 'primereact/multiselect';
import { FormEvent } from 'primereact/ts-helpers';
import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

type MaterialsFormFormProps = {
  initialValues: CourseFormState;
  state?: LoadingStateType;
  onSubmit: (data: FormikValues) => void;
  courseThumbnailData?: File;
  customAccountData?: Account;
  isCreate?: boolean;
  defaultLanguage?: string;
};

export const MaterialsForm: React.FC<MaterialsFormFormProps> = ({
  initialValues,
  state,
  onSubmit,
  courseThumbnailData,
  customAccountData,
  isCreate = false,
  defaultLanguage,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { setDialogData } = useContext(DialogContext);
  const account = useAppSelector(selectCurrentAccount);
  const user = useAppSelector(selectCurrentUser);
  const isSystem = account?.isSystem;
  const retired = !!initialValues.retired;
  const { cannot } = usePermission();
  const isBranchAdmin = branchAdminCheck(user, account);
  const isPoster = initialValues.type === MaterialType.POSTER;
  const isResource = initialValues.type === MaterialType.RESOURCE;
  const disabled: boolean =
    cannot(Actions.CREATE, Subjects.COURSES) || !isSystem || isBranchAdmin;
  const isDemoAccount = isDemoRestrictedMode(account, user);

  const [selectedTab, setSelectedTab] = useState(
    languageDisplayCode[defaultLanguage || LanguagesEnum.EN],
  );

  const [selectedCustomAccount, setSelectedCustomAccount] =
    useState<Account | null>(null);
  const [validOptions, setValidOptions] = useState([] as string[]);
  const [isSubmitDisabled, setIsSubmitDisabled] = useState<boolean>(false);

  useEffect(() => {
    if (customAccountData) {
      setSelectedCustomAccount(customAccountData);
    }
  }, [customAccountData]);

  useEffect(() => {
    setValidOptions(checkValidOptions(initialValues));
  }, [initialValues]);

  // Define required translations
  const requiredTranslations = (values: FormikValues) =>
    values.courseLanguages.map(
      (languageCode: LanguagesEnum) => languageDisplayCode[languageCode],
    );

  // Generate validOptions based on the current form values
  const checkValidOptions = (values: FormikValues) => {
    return availableLanguages
      .filter((languageCode) => {
        if (values.courseTranslations[languageCode]) {
          return values.courseTranslations[languageCode].name !== '';
        }
      })
      .map((languageCode) => languageDisplayCode[languageCode]);
  };

  const setLanguageOptions = (
    languageCode: LanguagesEnum,
    validOptions: string[],
    values: FormikValues,
    setFieldValue: (field: string, value: string[]) => void,
  ) => {
    if (
      validOptions.includes(languageDisplayCode[languageCode]) &&
      values.courseLanguages &&
      !values.courseLanguages.includes(languageCode)
    ) {
      setFieldValue('courseLanguages', [
        ...(values?.courseLanguages || []),
        languageCode,
      ]);
    }
  };

  const showDescription = (
    values: FormikValues,
    languageCode: string,
  ): boolean => {
    if (values.type === MaterialType.POSTER) return false;
    if (values.courseTranslations && !isSystem && !isCreate) {
      return !!values.courseTranslations[languageCode]?.description;
    }
    return true;
  };

  const cancelHandler = (values: CourseFormState) => {
    if (initialValues !== values || retired) {
      setDialogData({
        show: true,
        type: DialogType.CONFIRMATION,
        header: t(
          isCreate ? 'material.cancel.create' : 'material.cancel.update',
        ),
        message: t('content.cancel.confirm'),
        onAccept: () =>
          navigate(
            RedirectPaths[
              isResource
                ? RedirectPathsEnum.RESOURCES
                : RedirectPathsEnum.POSTERS
            ](),
          ),
      });
    } else {
      navigate(
        RedirectPaths[
          isResource ? RedirectPathsEnum.RESOURCES : RedirectPathsEnum.POSTERS
        ](),
      );
    }
  };

  const courseLanguagesOptionTemplate = (option: any) => {
    return <span>{option.name}</span>;
  };

  const handleChangeCourseLanguages = (
    event: MultiSelectChangeEvent,
    values: CourseFormState,
    setFieldValue: (field: string, value: string[]) => void,
  ) => {
    const options = event.value;
    setFieldValue('courseLanguages', options);

    if (values?.courseFiles) {
      values.courseFiles.forEach((item) => {
        Object.keys(item.translations).forEach((key) => {
          if (!options.includes(key)) {
            delete item.translations[key];
          }
        });
      });
    }

    Object.keys(values.courseTranslations)
      .filter((key: any) => !!values.courseTranslations[key].name)
      .forEach((key: any) => {
        if (!options.includes(key)) {
          (values.courseTranslations as any)[key].name = '';
          setValidOptions(checkValidOptions(values));
        }
      });

    if (
      !options.includes(values.defaultLanguage) &&
      values.defaultLanguage &&
      values.courseTranslations
    ) {
      (values.courseTranslations as any)[values.defaultLanguage].name = '';
      setValidOptions(checkValidOptions(values));
    }
  };

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      validationSchema={createCourseSchema(validOptions, t)}
      onSubmit={onSubmit}
      validateOnChange
      validateOnMount
    >
      {({ errors, setFieldValue, values, validateForm }) => {
        return (
          <FormContainer width={100}>
            {isCreate && (
              <h1 className="mt-0 mb-5">
                {isResource ? t('resources.new') : t('poster.new')}
              </h1>
            )}
            <Form className="w-full" autoComplete="off">
              <FlexContainer
                gap={24}
                width="100%"
                justify="space-between"
                align="flex-start"
              >
                <div className="w-full">
                  <FlexContainer className="mb-4" justify="flex-start">
                    <TabSwitchButtons
                      options={availableLanguages.map(
                        (languageCode) => languageDisplayCode[languageCode],
                      )}
                      requiredOptions={requiredTranslations(values)}
                      validOptions={validOptions}
                      onToggle={(option) => setSelectedTab(option as string)}
                      defaultSelected={selectedTab}
                    />
                  </FlexContainer>
                  {availableLanguages.map(
                    (languageCode) =>
                      selectedTab === languageDisplayCode[languageCode] && (
                        <div key={languageCode} className="w-full">
                          <div className="field mb-4">
                            <Field
                              disabled={disabled || retired}
                              id={`name-${languageCode}`}
                              label={t('generic.name')}
                              name={`courseTranslations.${languageCode}.name`}
                              className="w-full"
                              component={FormikInput}
                              placeholder={t('generic.name')}
                              required={requiredTranslations(values).includes(
                                languageCode,
                              )}
                              additionOnBlur={() => {
                                const validOptions = checkValidOptions(values);
                                setValidOptions(validOptions);
                                setLanguageOptions(
                                  languageCode,
                                  validOptions,
                                  values,
                                  setFieldValue,
                                );
                                setTimeout(async () => {
                                  await validateForm();
                                }, 0);
                              }}
                            />
                          </div>
                          {showDescription(values, languageCode) && (
                            <div className="field mb-0">
                              <Field
                                readOnly={disabled || retired}
                                id={`description-${languageCode}`}
                                label={t('generic.description')}
                                name={`courseTranslations.${languageCode}.description`}
                                className="w-full"
                                placeholder={t('generic.description')}
                                component={FormikRichTextArea}
                              />
                            </div>
                          )}
                        </div>
                      ),
                  )}
                  {isPoster && (
                    <div className="field w-full mb-4">
                      <Field
                        id="categories"
                        name="categories"
                        display="chip"
                        label={t('posters.categories')}
                        required
                        className="w-full"
                        component={FormikMultiSelect}
                        placeholder={t('generic.select')}
                        options={posterCategoriesOptions(t)}
                        disabled={disabled || retired}
                      />
                    </div>
                  )}
                  <div className="field mt-4">
                    <Field
                      disabled={disabled || retired}
                      id="course-languages"
                      name="courseLanguages"
                      filter
                      filterBy="name,label"
                      label={t('material.files.languages')}
                      className="w-full"
                      display="chip"
                      placeholder={t('generic.select')}
                      required={!values.releaseDate}
                      onChange={(event: MultiSelectChangeEvent) => {
                        handleChangeCourseLanguages(
                          event,
                          values,
                          setFieldValue,
                        );
                        setTimeout(async () => {
                          await validateForm();
                        }, 0);
                      }}
                      component={FormikMultiSelect}
                      options={courseLanguagesOptions.sort((a, b) =>
                        sortAlphabetically(a.name, b.name),
                      )}
                      itemTemplate={courseLanguagesOptionTemplate}
                    />
                  </div>

                  {(!disabled || initialValues.customAccount) && (
                    <CourseFiles
                      values={values}
                      label={t('generic.files')}
                      selfHosted
                      isCreate={isCreate}
                      isDisabled={disabled || retired}
                      setFieldValue={setFieldValue}
                      setIsSubmitDisabled={setIsSubmitDisabled}
                    />
                  )}

                  <div className="mb-5">
                    {!disabled && !initialValues.retired && (
                      <>
                        <AppButton
                          severity="secondary"
                          type="outlined"
                          label={t('button.cancel')}
                          className="mt-5 mr-3"
                          state={state}
                          onClick={() => cancelHandler(values)}
                        />

                        <AppButton
                          isSubmit={!isDemoAccount}
                          severity="success"
                          label={
                            isCreate ? t('button.create') : t('button.save')
                          }
                          className="mt-5"
                          state={state}
                          isDisabled={
                            !!Object.keys(errors).length || isSubmitDisabled
                          }
                        />
                      </>
                    )}
                  </div>
                </div>

                <div className="w-full">
                  <div className="field w-full mb-4">
                    <ImageUpload
                      label={t('generic.image')}
                      tooltip={
                        !isPoster
                          ? t('material.cover.image.suggested.ratio')
                          : undefined
                      }
                      onFileUpload={(file: File | null) =>
                        setFieldValue('courseThumbnail', file?.id)
                      }
                      purpose={FilePurpose.THUMBNAIL}
                      preselectedFile={courseThumbnailData}
                      setIsSubmitDisabled={setIsSubmitDisabled}
                      isDisabled={disabled || retired}
                    />
                  </div>

                  {!disabled && (
                    <AppFormAccordion label={t('material.accordion.header')}>
                      <div className="field mb-4">
                        <label htmlFor="course-created-at">
                          {t('generic.creation')}
                        </label>
                        <AppCalendar
                          disabled={retired}
                          id="course-created-at"
                          name="courseCreatedAt"
                          className="w-full"
                          showIcon
                          iconPos="left"
                          placeholder={t('generic.select')}
                          maxDate={values.courseLastUpdatedAt}
                          value={values.courseCreatedAt}
                          onChange={(event: FormEvent) =>
                            setFieldValue('courseCreatedAt', event.value)
                          }
                          showButtonBar
                          onClearButtonClick={() => {
                            setFieldValue('courseCreatedAt', undefined);
                          }}
                          readOnlyInput
                        />
                      </div>
                      <div className="field mb-4">
                        <label htmlFor="course-last-updated-at">
                          {t('generic.last.updated')}
                        </label>
                        <AppCalendar
                          disabled={retired}
                          id="course-last-updated-at"
                          name="courseLastUpdatedAt"
                          className="w-full"
                          showIcon
                          iconPos="left"
                          placeholder={t('generic.select')}
                          minDate={values.courseCreatedAt}
                          value={values.courseLastUpdatedAt}
                          onChange={(event: FormEvent) =>
                            setFieldValue('courseLastUpdatedAt', event.value)
                          }
                          showButtonBar
                          onClearButtonClick={() => {
                            setFieldValue('courseLastUpdatedAt', undefined);
                          }}
                          readOnlyInput
                        />
                      </div>
                      {(!initialValues.id ||
                        (initialValues.id && initialValues.customContent)) && (
                        <div className="field-checkbox mb-4">
                          <Field
                            disabled={
                              retired ||
                              (initialValues.customContent &&
                                !!initialValues.id) ||
                              values.isStandardCatalogue
                            }
                            inputId="customContent"
                            name="customContent"
                            label={t('generic.custom.content')}
                            value={!!values.customAccount}
                            onChange={(
                              e: React.ChangeEvent<HTMLInputElement>,
                            ) => {
                              setFieldValue('customContent', e.target.value);
                              if (!e.target.value) {
                                setFieldValue('customAccount', null);
                                setSelectedCustomAccount(null);
                              }
                              setTimeout(async () => {
                                await validateForm();
                              }, 0);
                            }}
                            component={FormikSwitch}
                          />
                        </div>
                      )}
                      {values.customContent && (
                        <div className="field">
                          <AccountSelectInput
                            isDisabled={
                              retired ||
                              (initialValues.customContent &&
                                !!initialValues.id) ||
                              values.isStandardCatalogue
                            }
                            defaultValue={selectedCustomAccount}
                            onSelect={(account) => {
                              if (account) {
                                setFieldValue('customAccount', account.id);
                                setSelectedCustomAccount(account);
                              }
                            }}
                            additionalFilters={[equal('isSystem', false)]}
                          />
                          <br />
                          {!values.customAccount && (
                            <small className="p-error">
                              {t('course.select.account')}
                            </small>
                          )}
                        </div>
                      )}
                      <label htmlFor="field-checkbox">
                        {t('generic.type')}
                      </label>

                      <FlexContainer justify="flex-start">
                        <div className="field-checkbox mt-2 mb-4">
                          <Field
                            disabled={retired || values.customContent}
                            inputId="isStandardCatalogue"
                            name="isStandardCatalogue"
                            label={t('content.isStandardCatalogue')}
                            component={FormikSwitch}
                            value={values.isStandardCatalogue}
                            onChange={(
                              e: React.ChangeEvent<HTMLInputElement>,
                            ) => {
                              setFieldValue(
                                'isStandardCatalogue',
                                e.target.value,
                              );
                            }}
                          />

                          <AppIconTooltip
                            target="isStandardCatalogue-icon"
                            content={t('course.tooltip.isStandardCatalogue')}
                          />
                        </div>
                      </FlexContainer>
                      {!isPoster && (
                        <>
                          <div className="field mb-4">
                            <Field
                              disabled={disabled || retired}
                              id="hubspotProperty"
                              label={t('course.hubspotProperty')}
                              name="hubspotProperty"
                              className="w-full"
                              component={FormikInput}
                              placeholder={t('course.hubspotProperty')}
                            />
                          </div>
                          <div className="field mb-4">
                            <Field
                              disabled={disabled || retired}
                              id="hubspotDateProperty"
                              label={t('course.hubspotDateProperty')}
                              name="hubspotDateProperty"
                              className="w-full"
                              component={FormikInput}
                              placeholder={t('course.hubspotDateProperty')}
                            />
                          </div>
                        </>
                      )}
                    </AppFormAccordion>
                  )}
                </div>
              </FlexContainer>
            </Form>
          </FormContainer>
        );
      }}
    </Formik>
  );
};
